<script setup lang="ts">
import type { UserSession } from '../internal/types.ts'
import { formatInstantDate, formatInstantTimeUtc } from '@taler/domain'
import { ErrorState, Skeleton, StatusTag } from '@taler/ui'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  sessions: readonly UserSession[]
  isLoading: boolean
  error: Error | null
  retrying?: boolean
  endingSessionId: string | undefined
  endSessionError: Error | null
  locale: 'en' | 'hu'
}>()

const emit = defineEmits<{
  end: [sessionId: string]
  retry: []
}>()

const { t } = useI18n()

function stamp(value: UserSession['createdAt']): string {
  return `${formatInstantDate(value, props.locale)} ${formatInstantTimeUtc(value)}`
}
</script>

<template>
  <section class="flex flex-col gap-[var(--space-block)]">
    <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
      {{ t('identityAdmin.sessionsTitle') }}
    </h2>

    <ul v-if="props.isLoading" aria-hidden="true" class="flex flex-col gap-3">
      <li v-for="index in 2" :key="index" class="flex items-center gap-3">
        <Skeleton width="60%" height="1rem" />
        <Skeleton width="20%" height="1rem" />
      </li>
    </ul>
    <p v-if="props.isLoading" class="sr-only" role="status">{{ t('identityAdmin.sessionsLoading') }}</p>

    <ErrorState
      v-else-if="props.error"
      :title="t('identityAdmin.sessionsErrorTitle')"
      :body="t('identityAdmin.sessionsErrorBody')"
      :retry-label="t('identityAdmin.retry')"
      :retrying="props.retrying"
      @retry="emit('retry')"
    />

    <p v-else-if="props.sessions.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('identityAdmin.sessionsEmpty') }}
    </p>

    <table v-else class="w-full border-collapse bg-[color:var(--color-surface)]">
      <thead>
        <tr
          class="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] text-left text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
        >
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('identityAdmin.device') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('identityAdmin.createdAt') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('identityAdmin.expiresAt') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            <span class="sr-only">{{ t('identityAdmin.endSession') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="session in props.sessions"
          :key="session.id"
          class="border-b border-[color:var(--color-border)]"
        >
          <td
            class="px-[var(--space-cell)] py-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
          >
            <p>{{ session.userAgent ?? t('identityAdmin.unknownDevice') }}</p>
            <p
              v-if="session.ipAddress"
              class="font-mono text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ session.ipAddress }}
            </p>
          </td>
          <td
            class="px-[var(--space-cell)] py-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
          >
            {{ stamp(session.createdAt) }}
          </td>
          <td
            class="px-[var(--space-cell)] py-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
          >
            {{ stamp(session.expiresAt) }}
          </td>
          <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-right">
            <button
              type="button"
              :disabled="props.endingSessionId === session.id"
              class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-3 py-1 text-[length:var(--text-step-2)] text-[color:var(--color-text-inverse)] disabled:opacity-60"
              @click="emit('end', session.id)"
            >
              {{
                props.endingSessionId === session.id
                  ? t('identityAdmin.endingSession')
                  : t('identityAdmin.endSession')
              }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <StatusTag v-if="props.endSessionError" tone="danger" role="alert">
      {{ t('identityAdmin.endSessionError') }}
    </StatusTag>

    <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
      {{ t('identityAdmin.endSessionNote') }}
    </p>
  </section>
</template>
